// Prospect Research management functions
// This is a shared resource accessible to all users
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const PROSPECTS_COLLECTION = 'prospects'

/**
 * Create a new prospect
 * @param {Object} prospectData - Prospect data (name, role, company, country, linkedinUrl, companySize, industry, source, tags)
 * @returns {Promise<string>} Document ID
 */
export const createProspect = async (prospectData) => {
  try {
    const docRef = await addDoc(collection(db, PROSPECTS_COLLECTION), {
      ...prospectData,
      tags: prospectData.tags || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating prospect:', error)
    throw error
  }
}

/**
 * Update an existing prospect
 * @param {string} prospectId - Prospect document ID
 * @param {Object} updates - Fields to update
 */
export const updateProspect = async (prospectId, updates) => {
  try {
    const docRef = doc(db, PROSPECTS_COLLECTION, prospectId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating prospect:', error)
    throw error
  }
}

/**
 * Delete a prospect
 * @param {string} prospectId - Prospect document ID
 */
export const deleteProspect = async (prospectId) => {
  try {
    const prospectRef = doc(db, PROSPECTS_COLLECTION, prospectId)
    await deleteDoc(prospectRef)
  } catch (error) {
    console.error('Error deleting prospect:', error)
    throw error
  }
}

/**
 * Get all prospects
 * @returns {Promise<Array>} Array of prospects
 */
export const getProspects = async () => {
  try {
    const q = query(
      collection(db, PROSPECTS_COLLECTION),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting prospects:', error)
    throw error
  }
}

/**
 * Subscribe to prospects changes (real-time)
 * This is a shared resource, so no user filtering
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToProspects = (callback) => {
  const q = query(
    collection(db, PROSPECTS_COLLECTION),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const prospects = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(prospects)
  }, (error) => {
    console.error('Error subscribing to prospects:', error)
  })
}
